import {
  CODE_RUN_TIMEOUT_MS,
  CODE_STDOUT_MAX,
  type CodeState,
} from "./codeLogic";
import {
  buildJsModuleSource,
  parsePythonPackages,
  pythonImportHint,
} from "./codePackages";
import { PYODIDE_WORKER_SOURCE } from "./pyodideWorkerSource";

export type CodeRunPhase =
  | "preparing"
  | "loading_runtime"
  | "loading_packages"
  | "running"
  | "done"
  | "error";

export type CodeRunCallbacks = {
  onPhase?: (phase: CodeRunPhase, detail?: string) => void;
  onOutput?: (stdout: string) => void;
};

export type CodeRunResult = {
  ok: boolean;
  stdout: string;
  error?: string;
};

/** Даём Pyodide шанс обработать SIGINT перед жёстким terminate. */
const PY_INTERRUPT_GRACE_MS = 400;

function clampOut(s: string): string {
  if (s.length <= CODE_STDOUT_MAX) return s;
  return s.slice(0, CODE_STDOUT_MAX) + "\n… (вывод обрезан)";
}

function withTimeout<T>(p: Promise<T>, ms: number, onTimeout: () => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => {
      onTimeout();
      reject(new Error(`Превышено время выполнения (${Math.round(ms / 1000)} с)`));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

/** JS в module Worker — import из белого списка; цикл убивается через terminate. */
export function runJavaScript(
  code: string,
  callbacks?: CodeRunCallbacks
): Promise<CodeRunResult> {
  callbacks?.onPhase?.("preparing");
  let moduleSrc: string;
  try {
    moduleSrc =
      buildJsModuleSource(code) +
      `\nglobalThis.__runResult = typeof __flushLogs === "function" ? __flushLogs() : "";`;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return Promise.resolve({ ok: false, stdout: "", error: msg });
  }

  const src = `
self.onmessage = async (ev) => {
  const moduleSrc = String(ev.data && ev.data.moduleSrc != null ? ev.data.moduleSrc : "");
  const blob = new Blob([moduleSrc], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  try {
    await import(url);
    const stdout = String(globalThis.__runResult || "");
    self.postMessage({ ok: true, stdout });
  } catch (e) {
    self.postMessage({
      ok: false,
      stdout: String(globalThis.__runResult || ""),
      error: String(e && e.message ? e.message : e),
    });
  } finally {
    URL.revokeObjectURL(url);
    globalThis.__runResult = "";
  }
};
`;
  const blob = new Blob([src], { type: "text/javascript" });
  const url = URL.createObjectURL(blob);
  const worker = new Worker(url, { type: "classic" });

  callbacks?.onPhase?.("running");
  const run = new Promise<CodeRunResult>((resolve) => {
    worker.onmessage = (ev) => {
      const data = ev.data || {};
      const stdout = clampOut(String(data.stdout || ""));
      callbacks?.onOutput?.(stdout);
      resolve({
        ok: Boolean(data.ok),
        stdout,
        error: data.error ? String(data.error) : undefined,
      });
    };
    worker.onerror = (err) => {
      resolve({ ok: false, stdout: "", error: err.message || "Worker error" });
    };
    worker.postMessage({ moduleSrc });
  });

  return withTimeout(run, CODE_RUN_TIMEOUT_MS, () => {
    try {
      worker.terminate();
    } catch {
      /* ignore */
    }
  })
    .finally(() => {
      URL.revokeObjectURL(url);
      try {
        worker.terminate();
      } catch {
        /* ignore */
      }
    })
    .catch((e: Error) => ({
      ok: false,
      stdout: "",
      error: e.message || String(e),
    }));
}

let pyodideWorker: Worker | null = null;
let pyodideWorkerUrl: string | null = null;
let pyInterruptBuffer: Uint8Array | null = null;

function getPyInterruptBuffer(): Uint8Array | null {
  if (pyInterruptBuffer) return pyInterruptBuffer;
  try {
    pyInterruptBuffer = new Uint8Array(new SharedArrayBuffer(1));
    return pyInterruptBuffer;
  } catch {
    return null;
  }
}

function ensurePyodideWorker(): Worker {
  if (pyodideWorker) return pyodideWorker;
  const blob = new Blob([PYODIDE_WORKER_SOURCE], { type: "application/javascript" });
  pyodideWorkerUrl = URL.createObjectURL(blob);
  pyodideWorker = new Worker(pyodideWorkerUrl);
  return pyodideWorker;
}

function killPyodideWorker(): void {
  if (pyodideWorker) {
    try {
      pyodideWorker.terminate();
    } catch {
      /* ignore */
    }
    pyodideWorker = null;
  }
  if (pyodideWorkerUrl) {
    URL.revokeObjectURL(pyodideWorkerUrl);
    pyodideWorkerUrl = null;
  }
  if (pyInterruptBuffer) {
    pyInterruptBuffer[0] = 0;
  }
}

/**
 * Python в отдельном Worker (Pyodide CDN).
 * Таймаут → SIGINT (если доступен SharedArrayBuffer) → terminate worker.
 * Вкладка не зависает при while True.
 */
export function runPython(code: string, callbacks?: CodeRunCallbacks): Promise<CodeRunResult> {
  const packages = parsePythonPackages(code);
  const worker = ensurePyodideWorker();
  const interruptBuffer = getPyInterruptBuffer();
  const chunks: string[] = [];
  callbacks?.onPhase?.("preparing");

  const flushOutput = () => {
    callbacks?.onOutput?.(clampOut(chunks.join("")));
  };

  return new Promise<CodeRunResult>((resolve) => {
    let settled = false;
    let timeoutTimer = 0;
    let killTimer = 0;

    const finish = (result: CodeRunResult) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutTimer);
      window.clearTimeout(killTimer);
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      callbacks?.onPhase?.(result.ok ? "done" : "error");
      resolve(result);
    };

    const onError = (err: ErrorEvent) => {
      killPyodideWorker();
      callbacks?.onPhase?.("error");
      finish({
        ok: false,
        stdout: clampOut(chunks.join("")),
        error: err.message || "Ошибка Python worker",
      });
    };

    const onMessage = (ev: MessageEvent) => {
      const msg = ev.data || {};
      if (msg.type === "phase") {
        const phase = String(msg.phase || "") as CodeRunPhase;
        if (
          phase === "loading_runtime" ||
          phase === "loading_packages" ||
          phase === "running"
        ) {
          callbacks?.onPhase?.(phase, msg.detail ? String(msg.detail) : undefined);
        }
        return;
      }
      if (msg.type === "stdout" || msg.type === "stderr") {
        chunks.push(String(msg.chunk || ""));
        flushOutput();
        return;
      }
      if (msg.type === "inputRequest") {
        const value = window.prompt(String(msg.prompt || ""));
        worker.postMessage({ type: "inputResponse", value: value ?? "" });
        return;
      }
      if (msg.type === "done") {
        if (interruptBuffer) interruptBuffer[0] = 0;
        finish({
          ok: Boolean(msg.ok),
          stdout: clampOut(chunks.join("") || (msg.ok ? "(нет вывода)" : "")),
          error: msg.error ? String(msg.error) : undefined,
        });
      }
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    timeoutTimer = window.setTimeout(() => {
      if (interruptBuffer) {
        interruptBuffer[0] = 2;
      }
      killTimer = window.setTimeout(() => {
        killPyodideWorker();
        finish({
          ok: false,
          stdout: clampOut(chunks.join("")),
          error: `Превышено время выполнения (${Math.round(
            CODE_RUN_TIMEOUT_MS / 1000
          )} с). Код остановлен — доска не зависает.`,
        });
      }, interruptBuffer ? PY_INTERRUPT_GRACE_MS : 0);
    }, CODE_RUN_TIMEOUT_MS);

    try {
      worker.postMessage({
        type: "run",
        code,
        packages,
        interruptBuffer,
      });
    } catch (e) {
      killPyodideWorker();
      const msg = e instanceof Error ? e.message : String(e);
      finish({
        ok: false,
        stdout: "",
        error: `Не удалось запустить Python: ${msg}. Доступны: ${pythonImportHint()}`,
      });
    }
  });
}

/** HTML+CSS(+JS) превью для iframe srcDoc. */
export function buildWebSrcDoc(html: string, css: string, js: string): string {
  const safeJs = js && js.trim() ? js : "";
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>
${css || ""}
</style>
</head>
<body>
${html || ""}
${safeJs ? `<script>\n${safeJs}\n</script>` : ""}
</body>
</html>`;
}

export async function runCodeWidget(
  state: CodeState,
  callbacks?: CodeRunCallbacks
): Promise<CodeRunResult> {
  if (state.language === "javascript") {
    return runJavaScript(state.js, callbacks);
  }
  if (state.language === "python") {
    return runPython(state.source, callbacks);
  }
  callbacks?.onPhase?.("running");
  callbacks?.onPhase?.("done");
  return {
    ok: true,
    stdout: "Превью HTML/CSS обновлено. JS в превью выполняется в sandbox iframe.",
  };
}
